import React, { useState } from "react";
import WebRTCStream from "./WebRTCStream";
import StreamSettingsDrawer from "./StreamSettingsDrawer";
import recordingService from "../services/recordingService";
import "./DashboardTab.css";
import "./CamerasTab.css";
import "./WebRTCStream.css";
import "./StreamSettingsDrawer.css";

const ChevronIcon = ({ open }) => (
  <svg className={open ? "chevron-open" : ""} viewBox="0 0 24 24">
    <path d="m9 18 6-6-6-6" />
  </svg>
);

const GearIcon = () => (
  <svg viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68 1.65 1.65 0 0 0 10 3.17V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

const CamerasTab = ({ streams = [], loading = false, error = null, onToggleDetection, onRefresh }) => {
  const [expandedItems, setExpandedItems] = useState({});
  const [layout, setLayout] = useState("list");
  const [recOpen, setRecOpen] = useState({});
  const [archives, setArchives] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});
  const [selectedRec, setSelectedRec] = useState({});
  const [deleting, setDeleting] = useState({});
  const [deleteErrors, setDeleteErrors] = useState({});
  const [settingsStream, setSettingsStream] = useState(null);
  const [addCameraOpen, setAddCameraOpen] = useState(false);

  const handleStreamError = (streamId, err) => {
    console.error(`Stream ${streamId} error:`, err);
  };

  const toggleExpanded = (streamId) => {
    setExpandedItems((prev) => ({ ...prev, [streamId]: !prev[streamId] }));
  };

  const loadRecordings = async (streamId, force = false) => {
    if (!force && archives[streamId]) return;
    setArchives((c) => ({ ...c, [streamId]: { loading: true, error: "", groups: [] } }));
    try {
      const groups = await recordingService.getRecordings(streamId);
      setArchives((c) => ({ ...c, [streamId]: { loading: false, error: "", groups: Array.isArray(groups) ? groups : [] } }));
    } catch (err) {
      setArchives((c) => ({ ...c, [streamId]: { loading: false, error: err.message, groups: [] } }));
    }
  };

  const toggleRecordings = (streamId) => {
    const willOpen = !recOpen[streamId];
    setRecOpen((c) => ({ ...c, [streamId]: willOpen }));
    if (willOpen) loadRecordings(streamId);
  };

  const toggleGroup = (streamId, date) => {
    const key = `${streamId}\x00${date}`;
    setExpandedGroups((c) => ({ ...c, [key]: !c[key] }));
  };

  const removeDeleted = (streamId, deleted) => {
    const keys = new Set(deleted.map((i) => `${i.date}\x00${i.filename}`));
    setArchives((c) => {
      const a = c[streamId];
      if (!a) return c;
      const groups = a.groups.map((g) => ({ ...g, files: (g.files || []).filter((f) => !keys.has(`${g.date}\x00${f.name}`)) })).filter((g) => g.files.length > 0);
      return { ...c, [streamId]: { ...a, groups } };
    });
  };

  const deleteOne = async (streamId, date, filename) => {
    if (!window.confirm(`Delete ${filename}?`)) return;
    const key = `${streamId}\x00${date}\x00${filename}`;
    setDeleting((c) => ({ ...c, [key]: true }));
    try {
      await recordingService.deleteRecording(streamId, date, filename);
      removeDeleted(streamId, [{ date, filename }]);
    } catch (err) {
      setDeleteErrors((c) => ({ ...c, [streamId]: err.message }));
    } finally {
      setDeleting((c) => ({ ...c, [key]: false }));
    }
  };

  const deleteAll = async (streamId, groups) => {
    const recs = groups.flatMap((g) => (g.files || []).map((f) => ({ date: g.date, filename: f.name })));
    if (!recs.length || !window.confirm(`Delete all ${recs.length} recordings?`)) return;
    const key = `all:${streamId}`;
    setDeleting((c) => ({ ...c, [key]: true }));
    try {
      const result = await recordingService.deleteRecordings(streamId, recs);
      removeDeleted(streamId, result.deleted || []);
    } catch (err) {
      setDeleteErrors((c) => ({ ...c, [streamId]: err.message }));
    } finally {
      setDeleting((c) => ({ ...c, [key]: false }));
    }
  };

  if (loading) {
    return (
      <div className="tab-content">
        <div className="tab-loading">Loading cameras...</div>
      </div>
    );
  }

  return (
    <div className="tab-content">
      <div className="layout-controls">
        <button className={`layout-btn ${layout === "list" ? "active" : ""}`} onClick={() => setLayout("list")}>
          <svg viewBox="0 0 24 24"><path d="M3 5h18M3 12h18M3 19h18" /></svg>
          List
        </button>
        <button className={`layout-btn ${layout === "tiles" ? "active" : ""}`} onClick={() => setLayout("tiles")}>
          <svg viewBox="0 0 24 24"><rect x="3" y="3" width="8" height="8" /><rect x="13" y="3" width="8" height="8" /><rect x="3" y="13" width="8" height="8" /><rect x="13" y="13" width="8" height="8" /></svg>
          Tiles
        </button>
        <button
          type="button"
          className="add-camera-btn"
          onClick={() => setAddCameraOpen(true)}
          title="Add camera"
        >
          <svg viewBox="0 0 24 24"><path d="M12 5v14M5 12h14" /></svg>
          Add Camera
        </button>
      </div>

      {error && <div className="tab-error">{error}</div>}

      {streams.length === 0 && (
        <div className="tab-empty">No cameras configured.</div>
      )}

      <div className={`streams-container layout-${layout}`}>
        {streams.map((stream) => {
          const archive = archives[stream.id];
          const count = archive?.groups?.reduce((t, g) => t + (g.files?.length || 0), 0) || 0;
          const isRecOpen = recOpen[stream.id];
          const deletingAll = Boolean(deleting[`all:${stream.id}`]);

          return (
            <div key={stream.id} className="stream-card">
              {layout === "list" && (
                <div className="stream-card-header">
                  <span className="stream-live-dot" />
                  <span className="stream-card-name" onClick={() => toggleExpanded(stream.id)}>
                    {stream.name}
                  </span>
                  {onToggleDetection && (
                    <button
                      type="button"
                      className={`stream-detection-toggle ${stream.detectionEnabled ? "enabled" : "disabled"}`}
                      onClick={() => onToggleDetection(stream.id, !stream.detectionEnabled)}
                      title={stream.detectionEnabled ? "Disable detection" : "Enable detection"}
                    >
                      {stream.detectionEnabled ? "Detection On" : "Detection Off"}
                    </button>
                  )}
                  <button
                    type="button"
                    className="stream-settings-btn"
                    onClick={() => setSettingsStream(stream)}
                    title="Camera settings"
                  >
                    <GearIcon />
                  </button>
                  <span className="stream-live-badge">Live</span>
                </div>
              )}
              <div className="stream-player">
                <WebRTCStream
                  streamId={stream.id}
                  streamName={stream.name}
                  streamUrl={stream.url}
                  onError={(err) => handleStreamError(stream.id, err)}
                  showControls={false}
                  expandedControls={
                    layout === "list" && expandedItems[stream.id]
                      ? { streamInfo: { id: stream.id, onDemand: stream.onDemand, disableAudio: stream.disableAudio, detectionEnabled: stream.detectionEnabled } }
                      : null
                  }
                />
              </div>

              {layout === "list" && (
                <div className="cam-rec-section">
                  <button type="button" className="cam-rec-toggle" onClick={() => toggleRecordings(stream.id)}>
                    <svg viewBox="0 0 24 24" className="cam-rec-icon"><path d="M3 6.5h6l2 2h9a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-11Z" /></svg>
                    <span>Recordings</span>
                    <span className="cam-rec-chevron"><ChevronIcon open={isRecOpen} /></span>
                  </button>

                  {isRecOpen && (
                    <div className="cam-rec-panel">
                      {archive?.loading && <div className="cam-rec-state"><span className="cam-rec-spinner" />Loading...</div>}
                      {archive?.error && (
                        <div className="cam-rec-state cam-rec-error">
                          {archive.error}
                          <button type="button" onClick={() => loadRecordings(stream.id, true)}>Retry</button>
                        </div>
                      )}
                      {!archive?.loading && !archive?.error && count === 0 && (
                        <div className="cam-rec-state">No recordings.</div>
                      )}
                      {count > 0 && (
                        <div className="cam-rec-actions">
                          <span>{count} recording{count === 1 ? "" : "s"}</span>
                          <button type="button" onClick={() => deleteAll(stream.id, archive.groups)} disabled={deletingAll}>
                            {deletingAll ? "Deleting..." : "Delete all"}
                          </button>
                        </div>
                      )}
                      {deleteErrors[stream.id] && <div className="cam-rec-error">{deleteErrors[stream.id]}</div>}

                      {selectedRec[stream.id] && (
                        <div className="cam-rec-player">
                          <div className="cam-rec-player-title"><span>Now playing</span> <strong>{selectedRec[stream.id].name}</strong></div>
                          <video key={selectedRec[stream.id].url} controls autoPlay playsInline preload="metadata" src={selectedRec[stream.id].url} />
                        </div>
                      )}

                      {!archive?.loading && !archive?.error && archive?.groups?.map((group) => {
                        const gk = `${stream.id}\x00${group.date}`;
                        const gOpen = Boolean(expandedGroups[gk]);
                        return (
                          <div className="cam-rec-group" key={group.date}>
                            <button type="button" className="cam-rec-group-toggle" onClick={() => toggleGroup(stream.id, group.date)}>
                              <span>{group.date}</span>
                              <small>{group.files?.length || 0}</small>
                              <span className="cam-rec-chevron"><ChevronIcon open={gOpen} /></span>
                            </button>
                            {gOpen && (
                              <div className="cam-rec-files">
                                {(group.files || []).map((file) => {
                                  const dk = `${stream.id}\x00${group.date}\x00${file.name}`;
                                  const isDel = Boolean(deleting[dk] || deletingAll);
                                  const isPlaying = selectedRec[stream.id]?.name === file.name && selectedRec[stream.id]?.date === group.date;
                                  return (
                                    <div className={`cam-rec-file ${isPlaying ? "is-playing" : ""}`} key={file.name}>
                                      <button
                                        type="button"
                                        className="cam-rec-file-open"
                                        onClick={() => setSelectedRec((c) => ({
                                          ...c,
                                          [stream.id]: { date: group.date, name: file.name, url: recordingService.getRecordingUrl(stream.id, group.date, file.name) },
                                        }))}
                                      >
                                        <span className="cam-rec-file-name">{file.name}</span>
                                        <span className="cam-rec-file-play">▶</span>
                                      </button>
                                      <button type="button" className="cam-rec-file-delete" onClick={() => deleteOne(stream.id, group.date, file.name)} disabled={isDel}>
                                        {isDel ? "..." : "✕"}
                                      </button>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <StreamSettingsDrawer
        open={!!settingsStream}
        stream={settingsStream}
        existingStreams={streams}
        onClose={() => setSettingsStream(null)}
        onSaved={() => { if (onRefresh) onRefresh(); }}
      />
      <StreamSettingsDrawer
        open={addCameraOpen}
        stream={null}
        existingStreams={streams}
        onClose={() => setAddCameraOpen(false)}
        onSaved={() => { if (onRefresh) onRefresh(); }}
      />
    </div>
  );
};

export default CamerasTab;
